// GENERATED FROM mobile_charts@a7062d0 lib/session-timeline.js
// sha256=f8a0e0c82e75dfe027dd9f9e86896d4c75f9529a943283482dced2c0d876ead5
// DO NOT EDIT HERE. Edit the mobile_charts original and re-run
// scripts/sync-vendored.sh — session logic MUST stay identical across the two products or they will disagree about which ticks are real.
// Session timeline — CAS (Closing Auction Session) support.
//
// From 2026-08-03 a stock's trading session depends on BOTH the symbol and the
// date: F&O-eligible cash equities stop continuous trading at 15:15 (was 15:30),
// while everything else in the same market segment keeps 15:30. Our symbolInfo
// builders take `session` from a per-SEGMENT blob, which cannot express either
// axis.
//
// TradingView's session grammar has a documented form for exactly this:
//
//     "0915-1530#20260803/0915-1515"
//      \________/ \______/ \_______/
//       old spec   switch    current spec (never carries a #date)
//
// `/` separates history entries, `#YYYYMMDD` is the day the session was
// rescheduled, and the LAST entry is the one in force now. Entries must be in
// ascending date order.
//
// Why this matters (measured against the real v32 library, see CAS-CHART-PLAN.md):
// stamping the new session on a symbol WITHOUT the history prefix makes TV merge
// every historical 15:16–15:29 bar into the 15:30 bar — 15 candles per day, every
// day of history, silently, with corrupted OHLC. The history prefix is what keeps
// pre-2026-08-03 days rendering exactly as they do today.
//
// Two hard rules learned the same way:
//   1. A MALFORMED session string does not throw a catchable error — it kills the
//      chart permanently (onChartReady never fires, spinner forever). TV's own
//      validators are dead code in v32, so we are the only validator. Hence
//      validateSessionString() and the fail-to-segment-default contract.
//   2. A MISSING session is safe (TV renders without session filtering), so
//      "omit" is a better last resort than "pass something dubious through".
//
// Everything here is pure: no window, no clock, no I/O. Times are IST (UTC+05:30,
// fixed — India has had no DST since 1945), matching odinDateParse().

var IST_OFFSET_MS = 19800000; // +05:30

// ---------------------------------------------------------------------------
// IST calendar helpers
// ---------------------------------------------------------------------------

// Epoch ms -> IST calendar parts. Deliberately NOT moment().startOf('day'),
// which resolves in the DEVICE timezone and is silently wrong for any non-IST
// viewer — a bug this module must not inherit, because it is used to DROP ticks.
export function istParts(epochMs) {
    var d = new Date(Number(epochMs) + IST_OFFSET_MS);
    var y = d.getUTCFullYear();
    var m = d.getUTCMonth() + 1;
    var day = d.getUTCDate();
    return {
        ymd: y * 10000 + m * 100 + day,                 // 20260803
        minuteOfDay: d.getUTCHours() * 60 + d.getUTCMinutes(),
        dayOfWeek: d.getUTCDay(),                       // 0=Sun .. 6=Sat
        seconds: d.getUTCSeconds(),
    };
}

// Start-of-IST-day for the day containing epochMs, as epoch ms.
export function istDayStartMs(epochMs) {
    var p = istParts(epochMs);
    return Date.UTC(Math.floor(p.ymd / 10000), (Math.floor(p.ymd / 100) % 100) - 1, p.ymd % 100) - IST_OFFSET_MS;
}

function ymdToDayOfWeek(ymd) {
    return new Date(Date.UTC(Math.floor(ymd / 10000), (Math.floor(ymd / 100) % 100) - 1, ymd % 100)).getUTCDay();
}

// ---------------------------------------------------------------------------
// Grammar
// ---------------------------------------------------------------------------
// We validate the NARROW subset we generate and that the backend actually uses
// today (verified against a live aux/symbol_segments payload):
//     spec        := range ("," range)*
//     range       := HHMM "-" HHMM
//     sessionStr  := (spec "#" YYYYMMDD "/")* spec
// Day masks (":1234567"), day-offsets ("F"/"Fn"), "|" sections and "24x7" are
// NOT generated by us. A string using them is not rejected as malformed — it is
// reported UNSUPPORTED so the caller passes the backend's own value through
// untouched rather than replacing it with a guess.

var RANGE_RE = /^(\d{4})-(\d{4})$/;
var DATE_RE = /^\d{8}$/;

function parseRange(text) {
    var m = RANGE_RE.exec(text);
    if (!m) return null;
    var sh = +m[1].slice(0, 2), sm = +m[1].slice(2);
    var eh = +m[2].slice(0, 2), em = +m[2].slice(2);
    if (sh > 47 || sm > 59 || eh > 47 || em > 59) return null;
    var start = sh * 60 + sm, end = eh * 60 + em;
    if (!(end > start)) return null;              // TV end is exclusive; zero/negative spans are invalid
    return { start: start, end: end };
}

function parseSpec(text) {
    if (!text) return null;
    var parts = String(text).split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
        var r = parseRange(parts[i].trim());
        if (!r) return null;
        out.push(r);
    }
    out.sort(function (a, b) { return a.start - b.start; });
    for (var j = 1; j < out.length; j++) {
        if (out[j].start < out[j - 1].end) return null;   // overlapping intervals
    }
    return out;
}

/**
 * Validate a session string.
 * @returns {{ok:true, entries:Array}} | {{ok:false, code:string, detail?:string}}
 *   codes: EMPTY | UNSUPPORTED | BAD_ENTRY | BAD_DATE | BAD_HISTORY_ORDER | TRAILING_DATE
 */
export function validateSessionString(str) {
    if (typeof str !== 'string' || str.trim() === '') return { ok: false, code: 'EMPTY' };
    var s = str.trim();
    // Tokens we never generate. Pass the backend's own string through untouched
    // rather than second-guessing a grammar we don't fully model.
    if (/[|;F]/.test(s) || /:/.test(s) || /24x7/i.test(s)) return { ok: false, code: 'UNSUPPORTED' };

    var chunks = s.split('/');
    var entries = [];
    var prevDate = -1;
    for (var i = 0; i < chunks.length; i++) {
        var isLast = (i === chunks.length - 1);
        var bits = chunks[i].split('#');
        if (isLast) {
            // The current entry must NOT carry a date.
            if (bits.length !== 1) return { ok: false, code: 'TRAILING_DATE', detail: chunks[i] };
        } else if (bits.length !== 2) {
            return { ok: false, code: 'BAD_ENTRY', detail: chunks[i] };
        }
        var spec = parseSpec(bits[0]);
        if (!spec) return { ok: false, code: 'BAD_ENTRY', detail: chunks[i] };
        var until = null;
        if (!isLast) {
            if (!DATE_RE.test(bits[1])) return { ok: false, code: 'BAD_DATE', detail: bits[1] };
            until = +bits[1];
            var mo = Math.floor(until / 100) % 100, da = until % 100;
            if (mo < 1 || mo > 12 || da < 1 || da > 31) return { ok: false, code: 'BAD_DATE', detail: bits[1] };
            if (until <= prevDate) return { ok: false, code: 'BAD_HISTORY_ORDER', detail: bits[1] };
            prevDate = until;
        }
        entries.push({ spec: spec, switchesOn: until });
    }
    return { ok: true, entries: entries };
}

// ---------------------------------------------------------------------------
// Interpreting a session for a given instant
// ---------------------------------------------------------------------------

/**
 * Compile a session string (+ holidays + corrections) into a reusable spec.
 * Never throws. On any problem returns {ok:false,...} and every predicate below
 * degrades to "no opinion" — we would rather draw a spurious candle than blank a
 * chart because a parser disagreed with the backend.
 */
export function compileSession(sessionStr, sessionHolidays, corrections) {
    var v = validateSessionString(sessionStr);
    if (!v.ok) return { ok: false, code: v.code, detail: v.detail };

    var holidays = Object.create(null);
    String(sessionHolidays || '').split(',').forEach(function (d) {
        d = d.trim();
        if (DATE_RE.test(d)) holidays[d] = true;
    });

    // "spec:YYYYMMDD,YYYYMMDD;spec:YYYYMMDD" — corrections outrank session_holidays.
    var corr = Object.create(null);
    String(corrections || '').split(';').forEach(function (group) {
        var idx = group.lastIndexOf(':');
        if (idx < 0) return;
        var spec = parseSpec(group.slice(0, idx).trim());
        if (!spec) return;
        group.slice(idx + 1).split(',').forEach(function (d) {
            d = d.trim();
            if (DATE_RE.test(d)) corr[d] = spec;
        });
    });

    // Snap each switch date to the first Monday on/after it, ONCE. TV starts a new
    // history entry at the week boundary, so we must mirror that or our guard and
    // TV's grid disagree for up to 6 days. Doing it here rather than per call keeps
    // the hot path (one isInSession per streamed tick) allocation-free.
    for (var i = 0; i < v.entries.length - 1; i++) {
        v.entries[i].effectiveFrom = mondayOnOrAfter(v.entries[i].switchesOn);
    }

    return { ok: true, entries: v.entries, holidays: holidays, corrections: corr, _lastYmd: 0, _lastIv: null };
}

// First Monday on or after `ymd`. Called once per entry at compile time.
function mondayOnOrAfter(ymd) {
    var dow = ymdToDayOfWeek(ymd);
    var delta = (dow === 1) ? 0 : ((8 - dow) % 7);            // 1=Monday
    if (delta === 0) return ymd;
    var d = new Date(Date.UTC(Math.floor(ymd / 10000), (Math.floor(ymd / 100) % 100) - 1, ymd % 100));
    d.setUTCDate(d.getUTCDate() + delta);
    return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}

/** Trading intervals (in minutes-of-day, IST) for a calendar date. [] = closed. */
export function intervalsForYmd(spec, ymd) {
    if (!spec || !spec.ok) return null;
    // Ticks arrive in time order, so a single-slot memo hits on all but the first
    // tick of each day. Without it every tick would re-key two hash lookups and
    // construct a Date, on every open subscription.
    if (spec._lastYmd === ymd) return spec._lastIv;
    var iv;
    var key = String(ymd);
    if (spec.corrections[key]) {
        iv = spec.corrections[key];                            // corrections outrank holidays
    } else if (spec.holidays[key]) {
        iv = [];
    } else {
        var dow = ymdToDayOfWeek(ymd);
        if (dow === 0 || dow === 6) {
            iv = [];                                           // weekends (no day-mask support needed today)
        } else {
            var entries = spec.entries;
            iv = entries[entries.length - 1].spec;
            for (var i = 0; i < entries.length - 1; i++) {
                if (ymd < entries[i].effectiveFrom) { iv = entries[i].spec; break; }
            }
        }
    }
    spec._lastYmd = ymd;
    spec._lastIv = iv;
    return iv;
}

/**
 * Is `epochMs` inside the symbol's trading session?
 * FAIL-OPEN: an uncompiled/invalid spec returns true. A guard that drops ticks
 * must never be the reason a chart goes silent.
 */
export function isInSession(spec, epochMs) {
    if (!spec || !spec.ok) return true;
    var p = istParts(epochMs);
    var iv = intervalsForYmd(spec, p.ymd);
    if (!iv) return true;
    for (var i = 0; i < iv.length; i++) {
        if (p.minuteOfDay >= iv[i].start && p.minuteOfDay < iv[i].end) return true;
    }
    return false;
}


// ---------------------------------------------------------------------------
// Market phase (pre-open / CAS) — for the pre/post-market price marker
// ---------------------------------------------------------------------------
// `isInSession` answers "may this tick build a candle?". This answers the
// complementary question "if it may NOT, is it worth showing anyway, and as
// what?" — which is exactly the pre/post-market marker TradingView draws on the
// price scale.
//
// The two are deliberately the same predicate seen from opposite sides. A CAS
// print at 15:28 is dropped from the candle BECAUSE it is out of session; that
// is the same fact that makes it the closing price worth surfacing separately.
// Measured on the 2026-08-12 CAS capture (HEROMOTOCO, NSE cash, F&O-eligible):
//
//   15:14:59  last continuous trade, LTP 5872.00   <- what the candle shows
//   15:15:01  tag 417 = 5883.00 (auction reference price) appears
//   15:20:00  tag  79 (volume) resets to 0, auction book opens
//   15:20:02  tag 418 = 5895.00 (indicative equilibrium price) appears
//   15:28:30  auction prints: LTP 5872.00 -> 5895.00   <- the real close
//
// The 15:28:30 tick is out of session (this symbol's session ends 15:15 under
// CAS), so the bar handler drops it and the chart's last candle stays at
// 5872.00 while the official close is 5895.00. That gap is what the marker
// exists to close.
//
// Pre-open is the mirror image: the exchange broadcasts the indicative price in
// tag 8 with high/low still at 0, and the auction prints around 09:07-09:08.
// Same out-of-session condition, same treatment. Note tag 417 does NOT appear
// in pre-open (zero on all 15 instruments in the 09:01-09:16 capture) — it is a
// closing-auction tag on this feed, so pre-market always falls back to the LTP.
// That is correct: in pre-open the LTP *is* the indicative price.

// How early before the open we call it pre-market rather than plain "closed".
//
// 15 makes the window exactly NSE's pre-open — 09:00-09:15 for an 0915 open,
// and 08:45-09:00 for MCX's 0900 open.
//
// This is a CHOSEN constant, not one anything supplies us: session_history gives
// the open and the close and nothing else, and no feed tag marks the start of
// pre-open. Choosing it wrong is cheap by construction — post-market runs right
// up to this boundary, so a tick on the wrong side still draws a marker, just
// labelled "Post" instead of "Pre". The failure mode is a wrong label, never a
// missing one.
//
// The one measurement we have (tick-capture-2026-08-12-0901-0916) begins at
// 09:01:39, so it confirms pre-open activity by 09:01 but cannot prove a 09:00
// start. To pin it exactly, capture from 08:50 and read the first packet.
var PRE_MARKET_LEAD_MIN = 15;

// There is deliberately no matching trailing cap. Post-market runs from the
// session end right up to the next pre-market window — i.e. it hands over to
// 'pre_market' at open-minus-PRE_MARKET_LEAD_MIN the following trading day.
//
// Why no cap: the discrepancy this surfaces (official close != last candle)
// is true for as long as the market is shut. The intraday candle is never
// corrected and the daily one only catches up at EOD, so any cap just picks an
// arbitrary moment to start lying. Two earlier attempts — 120 minutes, then
// end-of-IST-day — both failed the same way: a chart opened outside the window
// showed the stale number with nothing beside it, which is the exact case this
// was built for.
//
// Deriving the handover from the session (rather than hardcoding ~09:00) means
// MCX's 0900-2330 gets 23:30 -> 08:45 for free, and any session change adapts.
// Consequence: 'out_of_session' now means only a mid-day break in a split
// session. Weekends and holidays still short-circuit to 'holiday' above, so no
// badge is drawn on a non-trading day.

/**
 * Classify an instant against the symbol's session.
 * @returns 'pre_market' | 'market' | 'post_market' | 'out_of_session' | 'holiday'
 *
 * Values are TradingView's `current_session` enum verbatim — they are handed
 * straight to the quote object, where they drive both the pre/post-market price
 * marker and the legend's market-status pill.
 *
 * FAIL-CLOSED, unlike isInSession(): an uncompiled or invalid spec returns
 * 'market'. isInSession fails open because a wrong `false` blanks a chart;
 * here a wrong non-'market' would paint a marker that shouldn't exist, and
 * 'market' is the answer that changes nothing.
 */
export function sessionPhase(spec, epochMs) {
    if (!spec || !spec.ok) return 'market';
    var p = istParts(epochMs);
    var iv = intervalsForYmd(spec, p.ymd);
    if (!iv) return 'market';
    if (iv.length === 0) return 'holiday';
    var m = p.minuteOfDay;
    for (var i = 0; i < iv.length; i++) {
        if (m >= iv[i].start && m < iv[i].end) return 'market';
    }
    // intervalsForYmd hands back a spec sorted by start (parseSpec sorts and
    // rejects overlaps), so first/last bound the whole trading day.
    var first = iv[0];
    var last = iv[iv.length - 1];
    if (m < first.start) {
        // Close enough to the open to be the pre-open auction; otherwise still
        // "after the previous close" — the last traded price stays the closing
        // price until a new session discovers a new one.
        return (first.start - m) <= PRE_MARKET_LEAD_MIN ? 'pre_market' : 'post_market';
    }
    if (m >= last.end) {
        return 'post_market';
    }
    // Between two intervals of a split session (a lunch break). Not an auction
    // window — nothing to surface.
    return 'out_of_session';
}

/** Minute-of-day -> "HHMM", wrapping past midnight (so 1515+1440 -> "1515"). */
function hhmm(minuteOfDay) {
    var m = ((minuteOfDay % 1440) + 1440) % 1440;
    var h = Math.floor(m / 60), r = m % 60;
    return (h < 10 ? '0' : '') + h + (r < 10 ? '0' : '') + r;
}

/**
 * Build TradingView `subsessions` for a symbol, derived from its own session.
 *
 * THIS IS WHAT MAKES THE PRE/POST-MARKET MARKER WORK. `current_session` is not
 * an input TradingView accepts from the datafeed — it is an OUTPUT it computes.
 * Its market-state ticker (chartapi-local-transport) runs once a second:
 *
 *     let e = isTradingNow(now, this._tradeSession) ? "market" : "out_of_session";
 *     if (this._preSession  && isTradingNow(now, this._preSession))  e = "pre_market";
 *     if (this._postSession && isTradingNow(now, this._postSession)) e = "post_market";
 *
 * and pushes the result into the quote stream, where it merges over anything we
 * sent. _preSession/_postSession are populated ONLY from subsessions, and only
 * when `subsession_id` is set too:
 *
 *     if (t.subsession_id && undefined !== t.subsessions) { ...populate... }
 *     else this._preSession = undefined, this._postSession = undefined;
 *
 * Without them that ticker can only ever emit "market" or "out_of_session", so
 * the marker can never appear no matter what the datafeed sends. `rtc` is still
 * ours — TV never writes it — but the phase belongs to TV.
 *
 * Windows are derived, never hardcoded, so they track whatever session the
 * symbol actually has (CAS-shortened 0915-1515, F&O 0915-1540, MCX 0900-2330)
 * and stay consistent with sessionPhase(): pre-market is PRE_MARKET_LEAD_MIN
 * before the open, post-market runs from the close to the next pre-market. The
 * post-market string intentionally wraps past midnight (e.g. "1515-0900"),
 * which is TradingView's own overnight-session form.
 *
 * @returns array of subsessions, or null when the day has no session (holiday,
 *          weekend, unparseable spec) — callers must then leave symbolInfo alone.
 */
export function subsessionsFor(spec, sessionStr, epochMs) {
    if (!spec || !spec.ok || !sessionStr) return null;
    var iv = intervalsForYmd(spec, istParts(epochMs).ymd);
    if (!iv || iv.length === 0) return null;
    var open = iv[0].start;
    var close = iv[iv.length - 1].end;
    var preStart = open - PRE_MARKET_LEAD_MIN;
    // A session opening inside the lead window has nowhere to put pre-market,
    // and one that runs to midnight has nowhere to put post-market.
    if (preStart < 0 || close >= 1440 || close <= open) return null;
    // `private: true` on the two auction windows is load-bearing, not cosmetic.
    // Declaring subsessions makes TradingView offer a Session selector (Regular /
    // Pre-market / Post-market) on the price-scale menu. Choosing one sets
    // sessionId away from "regular", which is exactly the condition that HIDES
    // the marker (isRegularSessionId goes false), and asks /data/history for
    // auction bars it cannot serve — a dead end we would be inviting users into.
    //
    // The three predicates read the flag differently, which is what lets us have
    // it both ways:
    //   symbolHasSeveralSessions = subsessions.filter(s => !s.private).length > 1
    //       -> only `regular` counts, so the selector is not offered
    //   symbolHasPreOrPostMarket = subsessions.some(s => s.id === 'premarket' ...)
    //       -> ignores `private`, so TV still knows pre/post exist
    //   the market-state ticker's _preSession/_postSession population
    //       -> ignores `private`, so the phase is still computed
    return [
        { id: 'regular', description: 'Regular Trading Hours', session: sessionStr },
        { id: 'premarket', description: 'Pre-market', session: hhmm(preStart) + '-' + hhmm(open), private: true },
        // ':234567' is load-bearing. A TradingView session string carries a
        // day mask (1=Sun..7=Sat) and DEFAULTS TO ':23456' (Mon-Fri) when the
        // mask is omitted — the library spells out ':1234567' for its own 24/7
        // spec precisely because the default is not all days. For a session
        // that crosses midnight the day is the one the session ENDS on, so a
        // bare '1515-0900' has no instance ending Saturday, i.e. no instance
        // STARTING FRIDAY. isTradingNow() was therefore false every Friday
        // evening and the market-state ticker fell through to
        // 'out_of_session', so the marker never drew — with a perfectly good
        // `rtc` already delivered. Measured 2026-08-21 16:18 IST on NIFTY:
        // subsessions and subsession_id both present, rtc 24252, phase
        // out_of_session. Adding day 7 restores the Friday->Saturday instance.
        // Day 1 stays out: there is no Sunday close to carry into Monday.
        { id: 'postmarket', description: 'Post-market', session: hhmm(close) + '-' + hhmm(preStart) + ':234567', private: true },
    ];
}

/**
 * Attach TradingView subsessions to a resolved symbolInfo. Returns a COPY when
 * it adds them, and the input untouched otherwise — never mutates, because the
 * caller's object is the one cached in the per-symbol resolve memo.
 *
 * Call it at whatever point the FINAL session is known (after any per-symbol
 * CAS override), since the windows are derived from it. Vendored into flow-web
 * so both apps share one implementation.
 */
export function withSubsessions(info, epochMs) {
    if (!info || !info.session || info.subsessions) return info;
    var subs = subsessionsFor(
        compileSession(info.session, info.session_holidays, info.corrections),
        info.session, epochMs);
    if (!subs) return info;
    var out = Object.assign({}, info);
    out.subsessions = subs;
    out.subsession_id = 'regular';
    return out;
}

/**
 * Merge two `corrections` strings ("SPEC:YYYYMMDD,YYYYMMDD;SPEC:YYYYMMDD"),
 * `override` winning wherever both name the same date.
 *
 * Needed because a symbol now has corrections from TWO sources: the segment /
 * aux-symbol blob (muhurat sessions, half-days, exchange one-offs) and the
 * per-symbol `data/session_history` reply. Replacing one with the other loses
 * real trading days, so they have to be combined by date.
 *
 * Within a single string the LAST entry for a date wins, which the backend
 * already relies on — its CAS payload can carry e.g.
 *   "...;0915-1530:20260724;0915-1515:20260724"
 * where the second entry is the intended one. Parsing in order and overwriting
 * preserves that, and re-emitting deduped means TV never has to break the tie.
 */
export function mergeCorrections(base, override) {
    var byDate = Object.create(null);
    var dateOrder = [];
    [base, override].forEach(function (str) {
        String(str || '').split(';').forEach(function (group) {
            var idx = group.lastIndexOf(':');
            if (idx < 0) return;
            var spec = group.slice(0, idx).trim();
            if (!spec) return;
            group.slice(idx + 1).split(',').forEach(function (d) {
                d = d.trim();
                if (!DATE_RE.test(d)) return;
                if (!(d in byDate)) dateOrder.push(d);
                byDate[d] = spec;                     // later source / later entry wins
            });
        });
    });
    var bySpec = Object.create(null);
    var specOrder = [];
    dateOrder.forEach(function (d) {
        var spec = byDate[d];
        if (!bySpec[spec]) { bySpec[spec] = []; specOrder.push(spec); }
        bySpec[spec].push(d);
    });
    return specOrder.map(function (spec) { return spec + ':' + bySpec[spec].join(','); }).join(';');
}
